import { axiosInstance } from "../axios.config";

/** MKT-43: whether a listing posted now goes to the inviting Command company first */
export interface FirstLookEligibility {
  eligible: boolean;
  /** Set only when the operator invited the seller directly (Master §3.26) */
  companyName: string | null;
  named: boolean;
  /** When first look would end for a listing posted now, e.g. "Mon, Nov 2, 5:00 PM PST" */
  endsAt: string;
  endsAtLabel: string;
}

/** Admin-editable first-look settings (Tony's answers, Oct 31) */
export interface FirstLookSettings {
  cutoffHour: number;
  sameDayDueHour: number;
  nextDayDueHour: number;
  holidays: string[];
  passHoldMinutes: number;
  sellerResponseHours: number;
  operatorResponseHours: number;
  listingDays: number;
  /** Command link that gets first look when no operator is in the poster's inviter chain */
  assignedLinkId: string | null;
  timeoutsBeforeReassign: number;
  sellerOnHoldHours: number;
  badListingReviewHours: number;
  /** PRD 6.1: default offer expiry (hours) pre-filled on buyer offers */
  offerExpiryHours: number;
}

/** PRD 5.9: a listing an operator reported, waiting for admin review */
export interface FirstLookReport {
  listingId: string;
  address: string;
  sellerId: string;
  tenant: string;
  companyName: string;
  reason: string | null;
  reportedAt: string | null;
  reviewBy: string | null;
}

export interface CommandLink {
  id: string;
  marketplaceUserId: string;
  marketplaceUserEmail: string | null;
  marketplaceUserName: string | null;
  tenant: string;
  companyName: string;
  commandUserEmail: string;
  scope: "user" | "company";
  createdAt: string;
}

export interface CreateCommandLinkPayload {
  marketplaceUserEmail: string;
  tenant: string;
  commandUserEmail: string;
  scope: "user" | "company";
}

export const firstLookService = {
  async getEligibility(state?: string): Promise<FirstLookEligibility> {
    const response = await axiosInstance.get<FirstLookEligibility>("/first-look/eligibility", {
      params: state ? { state } : undefined,
    });
    return response.data;
  },

  async getSettings(): Promise<FirstLookSettings> {
    const response = await axiosInstance.get<FirstLookSettings>("/admin/first-look/settings");
    return response.data;
  },

  async updateSettings(payload: Partial<FirstLookSettings>): Promise<FirstLookSettings> {
    const response = await axiosInstance.put<FirstLookSettings>("/admin/first-look/settings", payload);
    return response.data;
  },

  async listCommandLinks(): Promise<CommandLink[]> {
    const response = await axiosInstance.get<CommandLink[]>("/admin/command-links");
    return response.data;
  },

  async createCommandLink(payload: CreateCommandLinkPayload): Promise<{ id: string }> {
    const response = await axiosInstance.post<{ id: string }>("/admin/command-links", payload);
    return response.data;
  },

  async deleteCommandLink(id: string): Promise<void> {
    await axiosInstance.delete(`/admin/command-links/${id}`);
  },

  async listReports(): Promise<FirstLookReport[]> {
    const response = await axiosInstance.get<FirstLookReport[]>("/admin/first-look/reports");
    return response.data;
  },

  async confirmReport(listingId: string): Promise<void> {
    await axiosInstance.post(`/admin/first-look/reports/${listingId}/confirm`);
  },

  async dismissReport(listingId: string): Promise<void> {
    await axiosInstance.post(`/admin/first-look/reports/${listingId}/dismiss`);
  },
};
